import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ChevronDownIcon, XIcon } from "@heroicons/react/outline";
import { CheckIcon } from "@heroicons/react/solid";
import { useAppDispatch, useAppSelector } from "../../../common/state/hooks";
import { useRouteNeedsUnloadedEntity } from "../hooks/useRouteNeedsUnloadedEntity";
import { getStepPosition, getTour } from "../registry";
import type { ChecklistItem } from "../registry/types";
import type { ChecklistProgress, ResumableTour } from "../state/onboardingSlice";
import {
  findChecklistItemForTour,
  findResumableForItem,
  findResumableTours,
  launchTour,
  resumeTour,
  selectOnboardingAudience,
  selectOnboardingRemote,
  selectLocationHintTourId,
  skipTour,
} from "../state/onboardingSlice";
import { ONBOARDING_PRIMARY_BUTTON, ONBOARDING_TEXT_BUTTON } from "./buttonClasses";
import { buildHelpDocsUrl } from "../../../common/util/helpDocsLighthouse/buildHelpDocsUrl";
import { HELPDOCS_HOME_PATH, WIXARPEDIA_ROUTE } from "./ChecklistCompletePrompt";

export type ChecklistCardProps = {
  progress: ChecklistProgress;
  resumable?: ResumableTour;
  onCollapse: () => void;
  onRequestDismiss: () => void;
};

const StatusIcon = ({ done }: { done: boolean }) =>
  done ? (
    <span className="h-5 w-5 shrink-0 rounded-full bg-green-500 flex items-center justify-center">
      <CheckIcon className="h-3.5 w-3.5 text-white" aria-hidden="true" />
    </span>
  ) : (
    <span className="h-5 w-5 shrink-0 rounded-full border-2 border-slate-300" aria-hidden="true" />
  );

export const ChecklistCard = ({
  progress,
  resumable,
  onCollapse,
  onRequestDismiss,
}: ChecklistCardProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const audience = useAppSelector(selectOnboardingAudience);
  const remote = useAppSelector(selectOnboardingRemote);
  const locationHintTourId = useAppSelector(selectLocationHintTourId);
  const { checklist, doneIds, doneCount, total, allDone } = progress;
  const [expandedId, setExpandedId] = useState<string | null>(
    () =>
      (resumable ? findChecklistItemForTour(progress, resumable.id)?.id : undefined) ??
      checklist.items.find((item) => !doneIds.has(item.id))?.id ??
      null,
  );
  const resumableTours = findResumableTours(remote, audience, Date.now());
  const needsRestart = useRouteNeedsUnloadedEntity();
  const percentage = total > 0 ? Math.round((doneCount / total) * 100) : 0;

  const handleDo = (route: string): void => {
    onCollapse();
    navigate(route);
  };

  const handleShowMe = (tourId: string): void => {
    void dispatch(launchTour({ id: tourId, source: "checklist" }));
  };

  const handleResume = (tourId: string): void => {
    void dispatch(resumeTour({ id: tourId, source: "checklist" }));
  };

  const handleRestart = (tourId: string): void => {
    void dispatch(launchTour({ id: tourId, source: "checklist", replay: true }));
  };

  const pendingLocationHint = (item: ChecklistItem) => {
    const pendingTour =
      item.tourId && item.tourId === locationHintTourId
        ? getTour(item.tourId, audience)
        : undefined;
    if (!pendingTour?.locationHintKey) {
      return null;
    }
    return <p className="mt-1 text-sm text-slate-500">{t(pendingTour.locationHintKey)}</p>;
  };

  const renderActions = (item: ChecklistItem, done: boolean) => {
    const { tourId, route } = item;
    let primary: { label: string; onClick: () => void } | null = null;
    if (tourId && findResumableForItem(resumableTours, item)) {
      const paused = findResumableForItem(resumableTours, item) as ResumableTour;
      const pausedTour = getTour(paused.id, audience);
      const label =
        resumable?.id === paused.id && pausedTour && !needsRestart(paused.route)
          ? t("onboarding.checklist.resumeWithProgress", getStepPosition(pausedTour, paused.step))
          : t("onboarding.checklist.resume");
      return (
        <div className="mt-3 flex items-center gap-3">
          <button
            type="button"
            className={ONBOARDING_PRIMARY_BUTTON}
            onClick={() => handleResume(paused.id)}
          >
            {label}
          </button>
          <button
            type="button"
            className={`${ONBOARDING_TEXT_BUTTON} text-slate-500 hover:text-slate-800`}
            onClick={() => void dispatch(skipTour({ id: paused.id, step: paused.step }))}
          >
            {t("onboarding.checklist.abandon")}
          </button>
        </div>
      );
    } else if (tourId && done) {
      return (
        <div className="mt-3">
          <button
            type="button"
            className={`${ONBOARDING_TEXT_BUTTON} text-slate-500 hover:text-slate-800`}
            onClick={() => handleRestart(tourId)}
          >
            {t("onboarding.checklist.restart")}
          </button>
        </div>
      );
    } else if (done) {
      return null;
    } else if (tourId) {
      primary = {
        label: t(item.actionKey ?? "onboarding.checklist.showMe"),
        onClick: () => handleShowMe(tourId),
      };
    } else if (route) {
      primary = {
        label: t(item.actionKey ?? "onboarding.checklist.do"),
        onClick: () => handleDo(route),
      };
    }
    if (!primary) {
      return null;
    }
    return (
      <div className="mt-3">
        <button type="button" className={ONBOARDING_PRIMARY_BUTTON} onClick={primary.onClick}>
          {primary.label}
        </button>
      </div>
    );
  };

  return (
    <div data-testid="onboarding-checklist-card" className="flex flex-col max-h-[min(560px,80vh)]">
      <div className="px-5 pt-5 pb-4 border-b border-slate-100">
        <div className="flex items-start justify-between gap-2">
          <h2 className="text-base font-semibold text-slate-900">
            {allDone ? t("onboarding.checklist.completedTitle") : t(checklist.titleKey)}
          </h2>
          <button
            type="button"
            className="-mt-1 -mr-2 h-8 w-8 shrink-0 rounded-md flex items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-slate-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-500"
            onClick={onRequestDismiss}
            aria-label={t("onboarding.checklist.dismissAriaLabel")}
          >
            <XIcon className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
        <p className="text-sm text-slate-500">
          {t("onboarding.checklist.progress", { done: doneCount, total })}
        </p>
        <div
          className="mt-3 h-1.5 rounded-full bg-slate-100 overflow-hidden"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={total}
          aria-valuenow={doneCount}
        >
          <div
            className="h-full rounded-full bg-green-500 transition-[width] duration-500"
            style={{ width: `${percentage}%` }}
          />
        </div>
      </div>
      {allDone ? (
        <div className="px-5 py-6 flex flex-col items-center text-center">
          <span className="h-12 w-12 rounded-full bg-green-50 flex items-center justify-center">
            <CheckIcon className="h-6 w-6 text-green-500" aria-hidden="true" />
          </span>
          <p className="mt-3 text-sm text-slate-600">{t(checklist.completedKey)}</p>
          <div className="mt-3 flex flex-wrap justify-center gap-x-4 gap-y-1">
            <a
              className={`${ONBOARDING_TEXT_BUTTON} text-green-800 hover:text-green-900`}
              href={buildHelpDocsUrl(HELPDOCS_HOME_PATH)}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("onboarding.checklist.completePrompt.docs")}
            </a>
            {audience !== "enduser" && (
              <button
                type="button"
                className={`${ONBOARDING_TEXT_BUTTON} text-green-800 hover:text-green-900`}
                onClick={() => {
                  onCollapse();
                  navigate(WIXARPEDIA_ROUTE);
                }}
              >
                {t("onboarding.checklist.completePrompt.wixarpedia")}
              </button>
            )}
          </div>
          <button
            type="button"
            className={`${ONBOARDING_PRIMARY_BUTTON} mt-4`}
            onClick={onCollapse}
          >
            {t("general.close")}
          </button>
        </div>
      ) : (
        <ul className="overflow-y-auto divide-y divide-slate-100">
          {checklist.items.map((item) => {
            const done = doneIds.has(item.id);
            const expanded = expandedId === item.id;
            const panelId = `onboarding-checklist-item-${item.id}`;
            let titleClass = "font-medium text-slate-800";
            if (done) {
              titleClass = "text-slate-400";
            } else if (expanded) {
              titleClass = "font-semibold text-slate-900";
            }
            return (
              <li key={item.id} className={expanded ? "bg-slate-50" : ""}>
                <button
                  type="button"
                  className="w-full flex items-center gap-3 px-5 py-3 text-left hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-green-500"
                  aria-expanded={expanded}
                  aria-controls={panelId}
                  onClick={() => setExpandedId(expanded ? null : item.id)}
                >
                  <StatusIcon done={done} />
                  <span className={`flex-1 text-sm ${titleClass}`}>
                    {t(item.labelKey)}
                    {done && <span className="sr-only"> ({t("onboarding.checklist.doneSr")})</span>}
                  </span>
                  <ChevronDownIcon
                    className={`h-4 w-4 shrink-0 text-slate-400 transition-transform duration-200 ${
                      expanded ? "rotate-180" : ""
                    }`}
                    aria-hidden="true"
                  />
                </button>
                {expanded && (
                  <div id={panelId} className="pl-[52px] pr-5 pb-4">
                    <p className="text-sm leading-5 text-slate-600 whitespace-pre-line">
                      {t(item.descriptionKey)}
                    </p>
                    {pendingLocationHint(item)}
                    {renderActions(item, done)}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default ChecklistCard;
